import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Card, Grid, MenuItem, Button, Typography, TextField } from "@mui/material";
import { styled, textAlign } from "@mui/system";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getDatabase, ref as dbRef, push, get, set } from "firebase/database";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { storage } from "../../../firebase";
import { Link } from "react-router-dom";
import MDButton from "components/MDButton";
import BasicLayout from "../components/BasicLayout";

const validationSchema = Yup.object({
  firstName: Yup.string().required("First Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  phoneNumber: Yup.string()
    .matches(/^\d{10}$/, "Phone number must be 10 digits")
    .required("Phone Number is required"),
  email: Yup.string().email("Invalid email format").required("Email is required"),
  state: Yup.string().required("State of residence is required"),
  profilePhoto: Yup.mixed().required("Profile Photo is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
});

const states = [
  "Abuja",
  "Abia",
  "Adamawa",
  "Akwa Ibom",
  "Anambra",
  "Bauchi",
  "Bayelsa",
  "Benue",
  "Borno",
  "Cross River",
  "Delta",
  "Ebonyi",
  "Edo",
  "Ekiti",
  "Enugu",
  "Gombe",
  "Imo",
  "Jigawa",
  "Kaduna",
  "Kano",
  "Katsina",
  "Kebbi",
  "Kogi",
  "Kwara",
  "Lagos",
  "Nasarawa",
  "Niger",
  "Ogun",
  "Ondo",
  "Osun",
  "Oyo",
  "Plateau",
  "Rivers",
  "Sokoto",
  "Taraba",
  "Yobe",
  "Zamfara",
];

const uploadImageToFirebase = async (file) => {
  if (!file) return null;
  const storageRef = ref(storage, `profilePhotos/${file.name}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
};

const AdminRegistrationForm = () => {
  const [uploading, setUploading] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      state: "",
      profilePhoto: null,
      password: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setUploading(true);
      try {
        const db = getDatabase();
        const adminRef = dbRef(db, "/adminLogin");

        const snapshot = await get(adminRef);
        if (snapshot.exists()) {
          const admins = snapshot.val();
          const emailExists = Object.values(admins).some((admin) => admin.email === values.email);
          if (emailExists) {
            toast.error("This email is already in use. Please use a different email.");
            setUploading(false);
            return;
          }
        }

        const imageUrl = await uploadImageToFirebase(values.profilePhoto);
        if (!imageUrl) throw new Error("Failed to upload image");

        const userKey = push(adminRef).key;

        const adminData = {
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
          email: values.email,
          imageUrl,
          password: values.password,
          state: values.state,
          adminAccess: "Pending",
          applicationReviewed: false,
          superAdmin: false,
          userKey,
        };

        await set(dbRef(db, `/adminLogin/${userKey}`), adminData);

        toast.success(
          "Your state agent registration was successful but is pending admin approval. Please log in to your state account in 30 minutes to sign in."
        );
        resetForm();
      } catch (error) {
        console.error("Error registering admin:", error);
        toast.error("Registration failed. Please try again.");
      } finally {
        setUploading(false);
      }
    },
  });

  return (
    <BasicLayout>
      <StyledCard>
        <ToastContainer />
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-8}
          mb={5}
          p={2}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Sign Up
          </MDTypography>
          <MDTypography variant="h6" textAlign="center" fontWeight="medium" color="white" mt={1}>
            Karzame
          </MDTypography>
        </MDBox>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            {["firstName", "lastName", "phoneNumber", "email", "password", "confirmPassword"].map(
              (field, index) => (
                <Grid item xs={12} sm={field.includes("Name") ? 6 : 12} key={index}>
                  <TextField
                    fullWidth
                    label={field.replace(/([A-Z])/g, " $1")}
                    type={field.includes("password") ? "password" : "text"}
                    name={field}
                    value={formik.values[field]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched[field] && Boolean(formik.errors[field])}
                    helperText={formik.touched[field] && formik.errors[field]}
                  />
                </Grid>
              )
            )}
            <Grid item xs={12}>
              <TextField
                select
                id="margin-none"
                fullWidth
                label="State of Residence"
                name="state"
                value={formik.values.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.state && Boolean(formik.errors.state)}
                helperText={formik.touched.state && formik.errors.state}
              >
                {states.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <input
                type="file"
                accept="image/*"
                onChange={(event) => formik.setFieldValue("profilePhoto", event.target.files[0])}
              />
              {formik.touched.profilePhoto && formik.errors.profilePhoto && (
                <Typography color="error" variant="caption">
                  {formik.errors.profilePhoto}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <MDButton
                variant="contained"
                color="info"
                fullWidth
                type="submit"
                disabled={uploading}
              >
                {uploading ? "Uploading..." : "Register Admin "}
              </MDButton>
            </Grid>
            <MDBox mt={3} mb={1}>
              <MDTypography variant="button" ml={10} color="text">
                Already have an account?
                <MDTypography
                  component={Link}
                  to="/authentication/sign-in"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign In
                </MDTypography>
              </MDTypography>
            </MDBox>
          </Grid>
        </form>
      </StyledCard>
    </BasicLayout>
  );
};

const StyledCard = styled(Card)({
  maxWidth: 500,
  margin: "auto",
  padding: 20,
  textAlign: "center",
  marginTop: "100px",
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  borderRadius: "10px",
});

export default AdminRegistrationForm;
